import { LanguageCode, Survey, SurveyData, SurveyQuestion, SurveyStatus, SurveyTheme } from "..";


export const surveyHelper = {
    getQuestionValue,
    getThemeValue,
    getValidLanguageCodes,
    getAllSurveyQuestions,
    getAllThemeQuestions,
    getThemeQuestionIndices,
    getThemeQuestionRanges,
    getSurveyQuestionIndex,
    getQuestionIndex,
    getAllIndicators,
    getAllIndicatorsWithCount,
    getSurveyStatus
};


function getQuestionValue(survey:Survey, themeIndex:number, questionIndex:number, languageCode:LanguageCode)
{
    if(languageCode === survey.languageCode)
        return survey.themes[themeIndex].questions[questionIndex].title;
    else
        return survey.themes[themeIndex].questions[questionIndex].translations.find(t => t.languageCode === languageCode)?.text || 'ERROR';
}

function getThemeValue(survey:Survey, themeIndex:number, languageCode:LanguageCode)
{
    if(languageCode === survey.languageCode)
        return survey.themes[themeIndex].name;
    else
        return survey.themes[themeIndex].translations.find(t => t.languageCode === languageCode)?.text || 'ERROR';
}

function getValidLanguageCodes(survey:SurveyData):LanguageCode[]
{
    return [
        survey.languageCode,
        ...survey.translationLanguages
    ].sort();
}

function getAllSurveyQuestions(survey: SurveyData): { themeIndex:number, question: SurveyQuestion }[] {
    return getAllThemeQuestions(survey.themes);
}

export function getAllThemeQuestions(themes:SurveyTheme[]): { themeIndex:number, question: SurveyQuestion }[] {
    return themes.flatMap((t,i) =>
        t.questions.map(q => {
            return { themeIndex:i, question: q }
        })
    );
}

export function getThemeQuestionIndices(survey: SurveyData, theme: string) {
    const themeIndex = survey.themes.findIndex(t => t.name === theme);
    if (themeIndex < 0)
        throw new Error(`Theme ${theme} does not exist`);

    let start = 0;
    for (let i = 0; i < themeIndex; i++)
        start += survey.themes[i].questions.length;

    return { start, length: survey.themes[themeIndex].questions.length };
}

// Including endIndexes means empty themes will have to be ignored (they don't fit in)
// (2) 0-1
// (0) 1-1 ? Wrong. It doesn't occupy any indexes
// (1) 2-2
// (2) 3-4
export function getThemeQuestionRanges(survey: SurveyData) {
    const ranges: { theme: string, startIndex: number, length: number }[] = [];

    let startIndex = 0;
    for (let themeIndex = 0; themeIndex < survey.themes.length; themeIndex++) {
        const length = survey.themes[themeIndex].questions.length
        ranges.push({
            theme: survey.themes[themeIndex].name,
            startIndex,
            length
        });
        startIndex = startIndex + length;
    }
    return ranges;
}

// Returns theme/question index for a given survey question total index
export function getSurveyQuestionIndex(survey: SurveyData, answerSelectionIndex: number) {
    const ranges = getThemeQuestionRanges(survey);
    for (let themeIndex = 0; themeIndex < survey.themes.length; themeIndex++) {
        const range = ranges[themeIndex];
        if (answerSelectionIndex >= range.startIndex
            && answerSelectionIndex < range.startIndex + range.length)
            return {
                themeIndex,
                questionIndex: answerSelectionIndex - range.startIndex
            };
        /// Something here
    }
    return null;
}

export function getQuestionIndex(survey:SurveyData, themeIndex:number, questionIndex:number)
{
    let i = 0;
    for(let ti=0; ti<survey.themes.length; ti++){
        for(let qi=0; qi<survey.themes[ti].questions.length; qi++){
            if(ti === themeIndex && qi === questionIndex)
                return i;
            i++;
        }
    }
    return -1;
}

export function getAllIndicators(survey: SurveyData) {
    return getAllIndicatorsWithCount(survey).map(i => i.indicator);
}

export function getAllIndicatorsWithCount(survey: SurveyData)
    :{indicator:string, count:number}[] 
{
    const allIndicators = survey.themes.flatMap(t => t.questions.flatMap(q => q.indicators));

    const counts:{indicator:string,count:number}[] = [];
    for(const i of allIndicators)
    {
        let counter = counts.find(c => c.indicator === i);
        if(!counter){
            counter = {indicator:i, count:0};
            counts.push(counter);
        }
        counter.count = counter.count+1;
    }
    counts.sort((lhs,rhs)=>lhs.indicator.localeCompare(rhs.indicator));

    return counts;
}

export function getSurveyStatus(survey: Survey): SurveyStatus {
    let access: 'waiting' | 'open' | 'closed';
    const now = new Date();
    access = 'waiting';
    if (now > survey.start) {
        access = 'open';
        if (now > survey.end) {
            access = 'closed';
        }
    }
    const allSurveyQuestions = getAllSurveyQuestions(survey);
    const hasQuestions = allSurveyQuestions.length > 0;
    const hasEmptyThemes = survey.themes.findIndex(t => t.questions.length === 0) >= 0;

    return {
        access,
        canEdit: survey.answerCount === 0,
        hasQuestions,
        canSubmitAnswer: access === 'open' && hasQuestions,
        warning: hasEmptyThemes ? 'empty theme(s)' : ''
    };
}